import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../AuthContext';
import { API_BASE_URL } from '../config';
import './EventLogsBlock.css';

// DJB-187: Event Logs detail-block on the Accounts Overview page. Backed by the
// ActivityController's /logbooks/search: scoped to the selected machine
// (globals excluded), with the top-right toggle mapping 1:1 onto the
// logsources filter. DJB-188 adds the manual-entry create form shown on the
// Manual toggle, persisting via POST /logbooks with source=MANUAL.
const PAGE_SIZE = 50;

const SOURCE_FILTERS = [
  { key: 'MANUAL', label: 'Manual' },
  { key: 'MACHINE', label: 'Machine' },
  { key: 'UNKNOWN', label: 'Unknown' },
  { key: 'ALL', label: 'All' },
];

const SOURCE_LABELS = { MANUAL: 'Manual', MACHINE: 'Machine', UNKNOWN: 'Unknown' };
const LEVEL_LABELS = { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' };

const EMPTY_DRAFT = { title: '', message: '', level: 'LOW' };

// The backend serializes LocalDateTime as [y, m, d, h, min, s(, nanos)] like
// the machine timestamps, but tolerate ISO strings should that config change
const toDate = (timestamp) => {
  if (Array.isArray(timestamp)) {
    if (timestamp.length < 3) return null;
    return new Date(
      timestamp[0], timestamp[1] - 1, timestamp[2],
      timestamp[3] || 0, timestamp[4] || 0, timestamp[5] || 0
    );
  }
  return timestamp ? new Date(timestamp) : null;
};

const formatEventTime = (timestamp) => {
  const date = toDate(timestamp);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('nl-BE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const EventLogsBlock = ({ machine }) => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const [activities, setActivities] = useState(null);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [lastMachineId, setLastMachineId] = useState(machine?.id);
  // DJB-188: manual-entry create form (only rendered on the Manual toggle)
  const [createOpen, setCreateOpen] = useState(true);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Switching accounts starts the block fresh: back to All, nothing picked.
  // Reset during render (not in an effect) so the fetch below never fires
  // with the previous account's filter still applied.
  if (machine?.id !== lastMachineId) {
    setLastMachineId(machine?.id);
    setSourceFilter('ALL');
    setSelectedEventId(null);
    setActivities(null);
    setError(null);
    setCreateOpen(true);
    setDraft(EMPTY_DRAFT);
    setCreateError(null);
  }

  useEffect(() => {
    if (!accessToken || !machine?.id) {
      return undefined;
    }
    let cancelled = false;
    setActivities(null);
    setError(null);
    const params = new URLSearchParams({
      machines: machine.id,
      includeGlobalActivities: 'false',
      page: '0',
      size: String(PAGE_SIZE),
    });
    if (sourceFilter !== 'ALL') {
      params.append('logsources', sourceFilter);
    }
    fetch(`${API_BASE_URL}/logbooks/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch event logs (${response.status})`);
        }
        return response.json();
      })
      .then((body) => {
        if (!cancelled) {
          setActivities(Array.isArray(body) ? body : []);
        }
      })
      .catch((fetchError) => {
        console.error('Failed to fetch /logbooks/search:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, machine?.id, sourceFilter, refreshKey]);

  // Newest at the top, by the same timestamp the Date column shows (the
  // backend orders by logtime; manual entries can be backdated via eventtime)
  const sortedActivities = useMemo(() => {
    if (!activities) return null;
    return [...activities].sort((a, b) => {
      const aTime = toDate(a.eventtime || a.logtime)?.getTime() ?? 0;
      const bTime = toDate(b.eventtime || b.logtime)?.getTime() ?? 0;
      return bTime - aTime;
    });
  }, [activities]);

  // The newest event shows on the right until a row is picked; a selection
  // that the active filter no longer contains also falls back to the newest
  const selectedEvent =
    sortedActivities?.find((activity) => activity.id === selectedEventId) ||
    sortedActivities?.[0] ||
    null;

  const handleFilterClick = (key) => {
    setSourceFilter(key);
    setCreateError(null);
    // Landing on Manual (re)opens the create form, even after a Cancel
    if (key === 'MANUAL') {
      setCreateOpen(true);
    }
  };

  const handleCreateCancel = () => {
    setDraft(EMPTY_DRAFT);
    setCreateError(null);
    setCreateOpen(false);
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    const subject = draft.title.trim();
    const content = draft.message.trim();
    // The backend rejects blank subjects and blank content, so gate here
    if (!subject || !content) {
      setCreateError('Please fill in both a title and a message.');
      return;
    }
    setCreateError(null);
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/logbooks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'MANUAL',
          machineId: machine.id,
          subject,
          content,
          loglevel: draft.level,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save manual entry (${response.status})`);
      }
      // The backend returns the saved DTO with its generated id and
      // server-stamped logtime/eventtime, so it can join the list directly
      const saved = await response.json().catch(() => null);
      setDraft(EMPTY_DRAFT);
      setCreateOpen(false);
      if (saved && saved.id) {
        setActivities((prev) => (prev ? [...prev, saved] : [saved]));
        setSelectedEventId(saved.id);
      } else {
        setRefreshKey((key) => key + 1);
      }
    } catch (postError) {
      console.error('Failed to POST /logbooks:', postError);
      setCreateError('Could not save the entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderCreateForm = () => {
    if (sourceFilter !== 'MANUAL') {
      return null;
    }
    if (!createOpen) {
      return (
        <button
          type="button"
          className="event-logs-create-reopen"
          onClick={() => setCreateOpen(true)}
        >
          + New manual entry
        </button>
      );
    }
    return (
      <form className="event-logs-create" aria-label="New manual entry" onSubmit={handleCreateSubmit}>
        <div className="event-logs-create-fields">
          <div className="event-logs-create-field event-logs-create-title">
            <label htmlFor="event-log-new-title">Title</label>
            <input
              id="event-log-new-title"
              type="text"
              value={draft.title}
              disabled={saving}
              onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="event-logs-create-field">
            <label htmlFor="event-log-new-level">Priority</label>
            <select
              id="event-log-new-level"
              value={draft.level}
              disabled={saving}
              onChange={(e) => setDraft((prev) => ({ ...prev, level: e.target.value }))}
            >
              {Object.entries(LEVEL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="event-logs-create-field event-logs-create-message">
            <label htmlFor="event-log-new-message">Message</label>
            <textarea
              id="event-log-new-message"
              rows={2}
              value={draft.message}
              disabled={saving}
              onChange={(e) => setDraft((prev) => ({ ...prev, message: e.target.value }))}
            />
          </div>
        </div>
        {createError && (
          <p className="event-logs-create-error" role="alert">{createError}</p>
        )}
        <div className="event-logs-create-actions">
          <button type="submit" className="event-logs-create-save" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="event-logs-create-cancel"
            disabled={saving}
            onClick={handleCreateCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  const renderList = () => {
    if (error) {
      return <p className="event-logs-note">Event logs are unavailable right now.</p>;
    }
    if (sortedActivities === null) {
      return <p className="event-logs-note">Loading event logs…</p>;
    }
    if (sortedActivities.length === 0) {
      return <p className="event-logs-note">No events for this machine.</p>;
    }
    return (
      <table className="event-logs-table">
        <thead>
          <tr>
            <th>Level</th>
            <th>Date</th>
            <th>Type</th>
            <th>Title</th>
          </tr>
        </thead>
        <tbody>
          {sortedActivities.map((activity) => {
            const level = (activity.loglevel || 'LOW').toUpperCase();
            const rowClasses = [
              activity.source === 'MANUAL' ? 'is-manual' : '',
              selectedEvent && activity.id === selectedEvent.id ? 'is-selected' : '',
            ].filter(Boolean).join(' ');
            return (
              <tr
                key={activity.id}
                className={rowClasses || undefined}
                onClick={() => setSelectedEventId(activity.id)}
              >
                <td className="event-level-cell">
                  <span className={`event-level-badge ${level.toLowerCase()}`}>
                    {LEVEL_LABELS[level] || level}
                  </span>
                </td>
                <td className="event-date-cell">{formatEventTime(activity.eventtime || activity.logtime)}</td>
                <td className="event-type-cell">{SOURCE_LABELS[activity.source] || 'Unknown'}</td>
                <td className="event-title-cell">{activity.subject || 'Activity'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderDetail = () => {
    if (!selectedEvent) {
      return <p className="event-logs-note">Select an event in the list to see its details.</p>;
    }
    const level = (selectedEvent.loglevel || 'LOW').toUpperCase();
    return (
      <div className="event-detail">
        <div className="event-detail-header">
          <span className={`event-level-badge ${level.toLowerCase()}`}>
            {LEVEL_LABELS[level] || level}
          </span>
          <h3 className="event-detail-title">{selectedEvent.subject || 'Activity'}</h3>
        </div>
        <div className="details-content">
          <p><strong>Type:</strong> {SOURCE_LABELS[selectedEvent.source] || 'Unknown'}</p>
          <p><strong>Event time:</strong> {formatEventTime(selectedEvent.eventtime)}</p>
          <p><strong>Logged at:</strong> {formatEventTime(selectedEvent.logtime)}</p>
        </div>
        <p className="info-section-label">Details</p>
        <p className="event-detail-content">{selectedEvent.content || '—'}</p>
      </div>
    );
  };

  return (
    <div className="details-block event-logs-block">
      <div className="details-title-row">
        <h2 className="details-title">Event Logs</h2>
        <div className="event-logs-filter" role="group" aria-label="Filter events by source">
          {SOURCE_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={'event-logs-filter-btn' + (sourceFilter === filter.key ? ' is-active' : '')}
              aria-pressed={sourceFilter === filter.key}
              onClick={() => handleFilterClick(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
      {renderCreateForm()}
      <div className="event-logs-body">
        <div className="event-logs-list-pane">{renderList()}</div>
        <div className="event-logs-detail-pane">{renderDetail()}</div>
      </div>
    </div>
  );
};

export default EventLogsBlock;
